/**
 * The PRISMA 2020 flow diagram for new systematic reviews.
 *
 * Box wording and layout follow the official templates "for new systematic reviews which
 * included searches of databases and registers only" and "…of databases, registers and
 * other sources" (Page et al., 2021, BMJ 372:n71; prisma-statement.org, CC BY 4.0): an
 * identification row, three screening rows and the included box, with the optional
 * "other methods" column — websites, organisations, citation searching — to the right.
 *
 * The counts are linked by subtraction at every step, and the template leaves that
 * arithmetic to the author. Here a blank step is filled in from the step before it, and
 * a typed number that disagrees with the arithmetic is reported, so a reviewer finds the
 * mismatch before a peer reviewer does.
 */

import {
  CHART_PADDING,
  layoutTitle,
  type Rect,
  type TitleBlock,
} from "@/lib/tool-kit/charts/layout";
import {
  estimateTextWidth,
  type TextStyle,
  wrapText,
} from "@/lib/tool-kit/charts/text";

/* -------------------------------------------------------------- the counts */

export interface PrismaSource {
  label: string;
  n: number | null;
}

export interface PrismaOther {
  sources: PrismaSource[];
  sought: number | null;
  notRetrieved: number | null;
  assessed: number | null;
  reasons: PrismaSource[];
}

/** What the reviewer typed. Null is a blank box. */
export interface PrismaInput {
  databases: number | null;
  databaseList: PrismaSource[];
  registers: number | null;
  registerList: PrismaSource[];
  duplicates: number | null;
  automation: number | null;
  otherRemoved: number | null;
  screened: number | null;
  excluded: number | null;
  sought: number | null;
  notRetrieved: number | null;
  assessed: number | null;
  reasons: PrismaSource[];
  studies: number | null;
  reports: number | null;
  /** The "other methods" column, or null to leave it out. */
  other: PrismaOther | null;
}

/** The numbers the diagram shows: typed, or worked out from the step before. */
export interface PrismaValues {
  databases: number | null;
  registers: number | null;
  identified: number | null;
  screened: number | null;
  sought: number | null;
  assessed: number | null;
  reports: number | null;
  otherIdentified: number | null;
  otherAssessed: number | null;
  /** Which of the above were worked out rather than typed. */
  calculated: ReadonlySet<CalculatedField>;
}

export type CalculatedField =
  | "databases"
  | "registers"
  | "screened"
  | "sought"
  | "assessed"
  | "reports"
  | "otherAssessed";

export type PrismaCheck =
  | {
      kind: "listTotal";
      field: "databases" | "registers";
      entered: number;
      sum: number;
    }
  | {
      kind: "mismatch";
      field: "screened" | "sought" | "assessed" | "reports" | "otherAssessed";
      entered: number;
      expected: number;
    }
  | {
      kind: "negative";
      field: "screened" | "sought" | "assessed" | "otherAssessed" | "reports";
      value: number;
    }
  | { kind: "studiesOverReports"; studies: number; reports: number }
  | { kind: "otherSoughtOverIdentified"; sought: number; identified: number }
  | {
      kind: "reasonsOverAssessed";
      column: "databases" | "other";
      reasons: number;
      assessed: number;
    };

/** The sum of the numbers given, or null when none is. */
export function sumKnown(values: readonly (number | null)[]): number | null {
  const known = values.filter((value): value is number => value !== null);
  return known.length === 0
    ? null
    : known.reduce((sum, value) => sum + value, 0);
}

function listSum(list: readonly PrismaSource[]): number | null {
  return sumKnown(list.map((source) => source.n));
}

interface Step {
  entered: number | null;
  expected: number | null;
}

/** The typed value, else the expected one; and whether it was worked out. */
function resolve(step: Step): { value: number | null; calculated: boolean } {
  if (step.entered !== null) {
    return { value: step.entered, calculated: false };
  }
  return { value: step.expected, calculated: step.expected !== null };
}

function minus(a: number | null, ...rest: (number | null)[]): number | null {
  if (a === null) {
    return null;
  }
  // Blank removals count as none removed.
  return rest.reduce<number>((left, value) => left - (value ?? 0), a);
}

function listChecks(input: PrismaInput, checks: PrismaCheck[]) {
  for (const field of ["databases", "registers"] as const) {
    const entered = input[field];
    const sum = listSum(
      field === "databases" ? input.databaseList : input.registerList
    );
    if (entered !== null && sum !== null && entered !== sum) {
      checks.push({ kind: "listTotal", field, entered, sum });
    }
  }
}

type StepField =
  | "screened"
  | "sought"
  | "assessed"
  | "reports"
  | "otherAssessed";

interface ResolvedSteps {
  values: Record<StepField, number | null>;
  calculated: Set<CalculatedField>;
  checks: PrismaCheck[];
}

/**
 * Walk the flow top to bottom. Each step is the typed number or, when blank, the one
 * before it minus what left the flow there; the next step builds on whichever was used.
 */
function resolveSteps(
  input: PrismaInput,
  identified: number | null
): ResolvedSteps {
  const calculated = new Set<CalculatedField>();
  const checks: PrismaCheck[] = [];
  const step = (
    field: StepField,
    entered: number | null,
    expected: number | null
  ) => {
    const resolved = resolve({ entered, expected });
    if (resolved.calculated) {
      calculated.add(field);
    }
    if (entered !== null && expected !== null && entered !== expected) {
      checks.push({ kind: "mismatch", field, entered, expected });
    }
    if (resolved.value !== null && resolved.value < 0) {
      checks.push({ kind: "negative", field, value: resolved.value });
    }
    return resolved.value;
  };
  const screened = step(
    "screened",
    input.screened,
    minus(identified, input.duplicates, input.automation, input.otherRemoved)
  );
  const sought = step(
    "sought",
    input.sought,
    screened !== null && input.excluded !== null
      ? screened - input.excluded
      : null
  );
  const assessed = step(
    "assessed",
    input.assessed,
    minus(sought, input.notRetrieved)
  );
  const other = input.other;
  const otherAssessed = other
    ? step(
        "otherAssessed",
        other.assessed,
        minus(other.sought, other.notRetrieved)
      )
    : null;
  const reasons = listSum(input.reasons) ?? 0;
  const otherReasons = other ? (listSum(other.reasons) ?? 0) : 0;
  const fromDatabases = assessed === null ? null : assessed - reasons;
  const fromOther =
    otherAssessed === null ? null : otherAssessed - otherReasons;
  let reportsExpected = fromDatabases;
  if (other) {
    reportsExpected =
      fromDatabases === null || fromOther === null
        ? null
        : fromDatabases + fromOther;
  }
  const reports = step("reports", input.reports, reportsExpected);
  if (assessed !== null && reasons > assessed) {
    checks.push({
      kind: "reasonsOverAssessed",
      column: "databases",
      reasons,
      assessed,
    });
  }
  if (otherAssessed !== null && otherReasons > otherAssessed) {
    checks.push({
      kind: "reasonsOverAssessed",
      column: "other",
      reasons: otherReasons,
      assessed: otherAssessed,
    });
  }
  return {
    values: { screened, sought, assessed, otherAssessed, reports },
    calculated,
    checks,
  };
}

/**
 * Work out the diagram's numbers and check that they add up. A typed number always
 * wins; the arithmetic only fills blanks and raises a check where the two disagree.
 */
export function computePrisma(input: PrismaInput): {
  values: PrismaValues;
  checks: PrismaCheck[];
} {
  const preChecks: PrismaCheck[] = [];
  listChecks(input, preChecks);
  const databaseSum = listSum(input.databaseList);
  const registerSum = listSum(input.registerList);
  const databases = input.databases ?? databaseSum;
  const registers = input.registers ?? registerSum;
  const identified = sumKnown([databases, registers]);
  const steps = resolveSteps(input, identified);
  const calculated = steps.calculated;
  if (input.databases === null && databaseSum !== null) {
    calculated.add("databases");
  }
  if (input.registers === null && registerSum !== null) {
    calculated.add("registers");
  }
  const checks = [...preChecks, ...steps.checks];
  const reports = steps.values.reports;
  if (input.studies !== null && reports !== null && input.studies > reports) {
    checks.push({
      kind: "studiesOverReports",
      studies: input.studies,
      reports,
    });
  }
  const otherIdentified = input.other ? listSum(input.other.sources) : null;
  const otherSought = input.other?.sought ?? null;
  if (
    otherSought !== null &&
    otherIdentified !== null &&
    otherSought > otherIdentified
  ) {
    checks.push({
      kind: "otherSoughtOverIdentified",
      sought: otherSought,
      identified: otherIdentified,
    });
  }
  return {
    values: {
      databases,
      registers,
      identified,
      screened: steps.values.screened,
      sought: steps.values.sought,
      assessed: steps.values.assessed,
      reports,
      otherIdentified,
      otherAssessed: steps.values.otherAssessed,
      calculated,
    },
    checks,
  };
}

const NUMBER_AT_END = /^(.*?)[\s,:;=(]*(?:n\s*=\s*)?(\d[\d\s.,']*)\)?\s*$/i;
const SEPARATOR_TAIL = /[\s,:;=(-]+$/;
const DIGIT_GROUPING = /[\s.,']/g;

/**
 * One "label, n" line — `MEDLINE, 812`, `Wrong population (n = 31)`, `Embase: 1,054`.
 * The number is the last one on the line, with any thousands separators.
 */
export function parseSourceLine(line: string): PrismaSource | null {
  const text = line.trim();
  if (text === "") {
    return null;
  }
  const match = NUMBER_AT_END.exec(text);
  if (!match) {
    return { label: text, n: null };
  }
  const label = match[1].replace(SEPARATOR_TAIL, "").trim();
  const n = Number(match[2].replace(DIGIT_GROUPING, ""));
  return { label, n: Number.isFinite(n) ? n : null };
}

const LINE_BREAK = /\r\n|\r|\n/;

export function parseSourceList(text: string): PrismaSource[] {
  return text
    .split(LINE_BREAK)
    .map(parseSourceLine)
    .filter((source): source is PrismaSource => source !== null);
}

/* ------------------------------------------------------------------ layout */

export interface PrismaLabels {
  headerDatabases: string;
  headerOther: string;
  phaseIdentification: string;
  phaseScreening: string;
  phaseIncluded: string;
  /** "Records identified from*:" — with the footnote mark. */
  identifiedFromMarked: string;
  identifiedFrom: string;
  databases: string;
  registers: string;
  removedBefore: string;
  duplicates: string;
  automation: string;
  otherRemoved: string;
  screened: string;
  /** "Records excluded**" — with the footnote mark. */
  excludedMarked: string;
  excluded: string;
  sought: string;
  notRetrieved: string;
  assessed: string;
  reportsExcluded: string;
  studies: string;
  reports: string;
  footnoteDatabases: string;
  footnoteAutomation: string;
  source: string;
  license: string;
}

export interface PrismaLayoutOptions {
  /** Draw the template's two footnotes and its source line under the diagram. */
  footnotes: boolean;
  /** Leave out count lines that were left blank, as the template allows. */
  hideBlank: boolean;
  title: string;
  /** "(n = 1,234)", or "(n = )" for null. */
  count: (value: number | null) => string;
}

export interface PrismaTextLine {
  text: string;
  x: number;
  y: number;
}

export type PrismaBoxId =
  | "identified"
  | "removed"
  | "screened"
  | "excluded"
  | "sought"
  | "notRetrieved"
  | "assessed"
  | "reportsExcluded"
  | "included"
  | "otherIdentified"
  | "otherSought"
  | "otherNotRetrieved"
  | "otherAssessed"
  | "otherExcluded";

export interface PrismaBox {
  id: PrismaBoxId;
  rect: Rect;
  lines: PrismaTextLine[];
}

export interface PrismaBand {
  rect: Rect;
  label: string;
  /** Phase bands are drawn upright, their label turned 90°. */
  vertical: boolean;
}

export interface PrismaLayout {
  width: number;
  height: number;
  title: TitleBlock | null;
  headers: PrismaBand[];
  phases: PrismaBand[];
  boxes: PrismaBox[];
  /** Polylines; each ends in an arrowhead. */
  arrows: [number, number][][];
  notes: PrismaTextLine[];
}

export const PRISMA_FONT = 11.5;
export const PRISMA_NOTE_FONT = 10;
const LINE = PRISMA_FONT * 1.25;
const NOTE_LINE = PRISMA_NOTE_FONT * 1.3;
const BOX_PAD = 8;
const INDENT = 10;
const ROW_GAP = 28;
const ARROW_GAP = 34;
const PHASE_WIDTH = 24;
const HEADER_HEIGHT = 26;
const MIN_BOX_HEIGHT = 44;

interface BoxLine {
  text: string;
  indent: number;
  /** "(n = 612)", kept whole: after the text when it fits, else on a line of its own. */
  count?: string;
}

interface Column {
  x: number;
  width: number;
}

function columns(other: boolean) {
  const main: Column = {
    x: CHART_PADDING + PHASE_WIDTH + 12,
    width: other ? 196 : 214,
  };
  const side: Column = {
    x: main.x + main.width + ARROW_GAP,
    width: other ? 196 : 226,
  };
  const otherMain: Column = { x: side.x + side.width + 40, width: 188 };
  const otherSide: Column = {
    x: otherMain.x + otherMain.width + ARROW_GAP,
    width: 188,
  };
  const right = other ? otherSide.x + otherSide.width : side.x + side.width;
  return { main, side, otherMain, otherSide, width: right + CHART_PADDING };
}

const STYLE: TextStyle = { fontSize: PRISMA_FONT };

/** Wrap a box's lines to its width; returns positioned lines and the box height. */
/** A box line wrapped to `room`, with its count never split across lines. */
function wrapCounted(line: BoxLine, room: number): string[] {
  const pieces = line.text ? wrapText(line.text, room, STYLE) : [];
  if (!line.count) {
    return pieces;
  }
  const last = pieces.at(-1);
  const joined = last ? `${last} ${line.count}` : line.count;
  if (estimateTextWidth(joined, STYLE) <= room) {
    return [...pieces.slice(0, -1), joined];
  }
  return [...pieces, line.count];
}

function fillBox(lines: readonly BoxLine[], column: Column, top: number) {
  const placed: PrismaTextLine[] = [];
  let y = top + BOX_PAD;
  for (const line of lines) {
    const x = column.x + BOX_PAD + line.indent * INDENT;
    const room = column.width - 2 * BOX_PAD - line.indent * INDENT;
    for (const piece of wrapCounted(line, room)) {
      y += LINE;
      placed.push({ text: piece, x, y: y - PRISMA_FONT * 0.3 });
    }
  }
  return { lines: placed, height: Math.max(MIN_BOX_HEIGHT, y - top + BOX_PAD) };
}

/** A label and its count on two lines, as the template sets them. */
function counted(label: string, value: string): BoxLine[] {
  return [
    { text: label, indent: 0 },
    { text: value, indent: 0 },
  ];
}

function sourceLines(
  list: readonly PrismaSource[],
  options: PrismaLayoutOptions,
  indent: number
): BoxLine[] {
  return list
    .filter((source) => source.label !== "" || source.n !== null)
    .map((source) => ({
      text: source.label,
      indent,
      count: source.n === null ? undefined : options.count(source.n),
    }));
}

interface BoxContent {
  id: PrismaBoxId;
  column: Column;
  lines: BoxLine[];
}

function identificationBoxes(
  input: PrismaInput,
  values: PrismaValues,
  labels: PrismaLabels,
  options: PrismaLayoutOptions,
  cols: ReturnType<typeof columns>
): BoxContent[] {
  const show = (value: number | null) => !options.hideBlank || value !== null;
  const identified: BoxLine[] = [
    {
      text: options.footnotes
        ? labels.identifiedFromMarked
        : labels.identifiedFrom,
      indent: 0,
    },
  ];
  if (show(values.databases) || input.databaseList.length > 0) {
    identified.push({
      text: labels.databases,
      indent: 1,
      count: options.count(values.databases),
    });
    identified.push(...sourceLines(input.databaseList, options, 2));
  }
  if (show(values.registers) || input.registerList.length > 0) {
    identified.push({
      text: labels.registers,
      indent: 1,
      count: options.count(values.registers),
    });
    identified.push(...sourceLines(input.registerList, options, 2));
  }
  const removed: BoxLine[] = [{ text: labels.removedBefore, indent: 0 }];
  for (const [label, value] of [
    [labels.duplicates, input.duplicates],
    [labels.automation, input.automation],
    [labels.otherRemoved, input.otherRemoved],
  ] as const) {
    if (show(value)) {
      removed.push({ text: label, indent: 1, count: options.count(value) });
    }
  }
  const boxes: BoxContent[] = [
    { id: "identified", column: cols.main, lines: identified },
    { id: "removed", column: cols.side, lines: removed },
  ];
  if (input.other) {
    boxes.push({
      id: "otherIdentified",
      column: cols.otherMain,
      lines: [
        { text: labels.identifiedFrom, indent: 0 },
        ...sourceLines(input.other.sources, options, 1),
      ],
    });
  }
  return boxes;
}

function reasonLines(
  reasons: readonly PrismaSource[],
  label: string,
  options: PrismaLayoutOptions
): BoxLine[] {
  return [{ text: label, indent: 0 }, ...sourceLines(reasons, options, 1)];
}

function screeningRows(
  input: PrismaInput,
  values: PrismaValues,
  labels: PrismaLabels,
  options: PrismaLayoutOptions,
  cols: ReturnType<typeof columns>
): BoxContent[][] {
  const n = options.count;
  const other = input.other;
  const rows: BoxContent[][] = [
    [
      {
        id: "screened",
        column: cols.main,
        lines: counted(labels.screened, n(values.screened)),
      },
      {
        id: "excluded",
        column: cols.side,
        lines: counted(
          options.footnotes ? labels.excludedMarked : labels.excluded,
          n(input.excluded)
        ),
      },
    ],
    [
      {
        id: "sought",
        column: cols.main,
        lines: counted(labels.sought, n(values.sought)),
      },
      {
        id: "notRetrieved",
        column: cols.side,
        lines: counted(labels.notRetrieved, n(input.notRetrieved)),
      },
    ],
    [
      {
        id: "assessed",
        column: cols.main,
        lines: counted(labels.assessed, n(values.assessed)),
      },
      {
        id: "reportsExcluded",
        column: cols.side,
        lines: reasonLines(input.reasons, labels.reportsExcluded, options),
      },
    ],
  ];
  if (other) {
    rows[1].push(
      {
        id: "otherSought",
        column: cols.otherMain,
        lines: counted(labels.sought, n(other.sought)),
      },
      {
        id: "otherNotRetrieved",
        column: cols.otherSide,
        lines: counted(labels.notRetrieved, n(other.notRetrieved)),
      }
    );
    rows[2].push(
      {
        id: "otherAssessed",
        column: cols.otherMain,
        lines: counted(labels.assessed, n(values.otherAssessed)),
      },
      {
        id: "otherExcluded",
        column: cols.otherSide,
        lines: reasonLines(other.reasons, labels.reportsExcluded, options),
      }
    );
  }
  return rows;
}

function boxOf(
  boxes: readonly PrismaBox[],
  id: PrismaBoxId
): PrismaBox | undefined {
  return boxes.find((box) => box.id === id);
}

function down(from: PrismaBox, to: PrismaBox): [number, number][] {
  const x = from.rect.x + from.rect.width / 2;
  return [
    [x, from.rect.y + from.rect.height],
    [x, to.rect.y],
  ];
}

function across(from: PrismaBox, to: PrismaBox): [number, number][] {
  const y = from.rect.y + Math.min(from.rect.height, to.rect.height) / 2;
  return [
    [from.rect.x + from.rect.width, y],
    [to.rect.x, y],
  ];
}

function arrowsFor(boxes: readonly PrismaBox[]): [number, number][][] {
  const get = (id: PrismaBoxId) => boxOf(boxes, id);
  const arrows: [number, number][][] = [];
  const pairs: [PrismaBoxId, PrismaBoxId, "down" | "across"][] = [
    ["identified", "screened", "down"],
    ["screened", "sought", "down"],
    ["sought", "assessed", "down"],
    ["assessed", "included", "down"],
    ["identified", "removed", "across"],
    ["screened", "excluded", "across"],
    ["sought", "notRetrieved", "across"],
    ["assessed", "reportsExcluded", "across"],
    ["otherIdentified", "otherSought", "down"],
    ["otherSought", "otherAssessed", "down"],
    ["otherSought", "otherNotRetrieved", "across"],
    ["otherAssessed", "otherExcluded", "across"],
  ];
  for (const [fromId, toId, direction] of pairs) {
    const from = get(fromId);
    const to = get(toId);
    if (from && to) {
      arrows.push(direction === "down" ? down(from, to) : across(from, to));
    }
  }
  const otherAssessed = get("otherAssessed");
  const included = get("included");
  if (otherAssessed && included) {
    const x = otherAssessed.rect.x + otherAssessed.rect.width / 2;
    const y = included.rect.y + included.rect.height / 2;
    arrows.push([
      [x, otherAssessed.rect.y + otherAssessed.rect.height],
      [x, y],
      [included.rect.x + included.rect.width, y],
    ]);
  }
  return arrows;
}

function noteLines(
  labels: PrismaLabels,
  width: number,
  top: number
): { lines: PrismaTextLine[]; bottom: number } {
  const style: TextStyle = { fontSize: PRISMA_NOTE_FONT };
  const lines: PrismaTextLine[] = [];
  let y = top;
  for (const note of [
    labels.footnoteDatabases,
    labels.footnoteAutomation,
    labels.source,
    labels.license,
  ]) {
    for (const piece of wrapText(note, width - 2 * CHART_PADDING, style)) {
      y += NOTE_LINE;
      lines.push({ text: piece, x: CHART_PADDING, y });
    }
    y += 3;
  }
  return { lines, bottom: y };
}

/** Lay the template out with the reviewer's numbers in it. */
export function layoutPrisma(
  input: PrismaInput,
  values: PrismaValues,
  labels: PrismaLabels,
  options: PrismaLayoutOptions
): PrismaLayout {
  const cols = columns(input.other !== null);
  const width = cols.width;
  const title = layoutTitle(options.title, width);
  const headerTop = CHART_PADDING + (title?.height ?? 0);
  const headers: PrismaBand[] = [
    {
      rect: {
        x: cols.main.x,
        y: headerTop,
        width: cols.side.x + cols.side.width - cols.main.x,
        height: HEADER_HEIGHT,
      },
      label: labels.headerDatabases,
      vertical: false,
    },
  ];
  if (input.other) {
    headers.push({
      rect: {
        x: cols.otherMain.x,
        y: headerTop,
        width: cols.otherSide.x + cols.otherSide.width - cols.otherMain.x,
        height: HEADER_HEIGHT,
      },
      label: labels.headerOther,
      vertical: false,
    });
  }
  const contentRows: BoxContent[][] = [
    identificationBoxes(input, values, labels, options, cols),
    ...screeningRows(input, values, labels, options, cols),
    [
      {
        id: "included",
        column: cols.main,
        lines: [
          ...counted(labels.studies, options.count(input.studies)),
          ...counted(labels.reports, options.count(values.reports)),
        ],
      },
    ],
  ];
  const boxes: PrismaBox[] = [];
  const rowSpans: { top: number; bottom: number }[] = [];
  let top = headerTop + HEADER_HEIGHT + 18;
  for (const row of contentRows) {
    const filled = row.map((content) => ({
      content,
      ...fillBox(content.lines, content.column, top),
    }));
    const height = Math.max(...filled.map((box) => box.height));
    for (const box of filled) {
      boxes.push({
        id: box.content.id,
        rect: {
          x: box.content.column.x,
          y: top,
          width: box.content.column.width,
          height: box.height,
        },
        lines: box.lines,
      });
    }
    rowSpans.push({ top, bottom: top + height });
    top += height + ROW_GAP;
  }
  const phase = (from: number, to: number, label: string): PrismaBand => ({
    rect: {
      x: CHART_PADDING,
      y: rowSpans[from].top,
      width: PHASE_WIDTH,
      height: rowSpans[to].bottom - rowSpans[from].top,
    },
    label,
    vertical: true,
  });
  const phases = [
    phase(0, 0, labels.phaseIdentification),
    phase(1, 3, labels.phaseScreening),
    phase(4, 4, labels.phaseIncluded),
  ];
  const diagramBottom = (rowSpans.at(-1)?.bottom ?? top) + 6;
  const notes = options.footnotes
    ? noteLines(labels, width, diagramBottom + 10)
    : { lines: [], bottom: diagramBottom };
  return {
    width,
    height: Math.ceil(notes.bottom + CHART_PADDING),
    title,
    headers,
    phases,
    boxes,
    arrows: arrowsFor(boxes),
    notes: notes.lines,
  };
}
